'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'next/navigation';
import { DynamicExperience } from '@highereducation/dynamic-experience';
import type {
  DCSContext,
  FlowContextParams,
  SlotPlacementId,
} from '@highereducation/dynamic-experience';
import { useMonarchSlotPlacement } from '@hen-site/app/hooks/useMonarchSlotData';
import { DynamicExperienceErrorBoundary } from '@hen-site/component/DynamicExperienceErrorBoundary';
import { useGlobalProps } from '@hen-site/component/GlobalContextProvider';
import { useRequestContextBridge } from '@hen-site/lib/request-context/RequestContextBridge';
import { getEnvironment } from '@hen-site/utils/getEnvironment';
import { logger } from '@hen-site/lib/logger/client';
import { newrelic } from '@hen-site/lib/newrelic/client';
import {
  resolveEffectiveForcedFlowId,
  type MonarchSlotFlowData,
} from './resolveEffectiveForcedFlowId';
import { collectContextParams } from './collectContextParams';
import { dcsContextFromParams } from '@hen-site/utils/articleContextualDefaults';
import { buildDynamicExperienceTenant } from '@hen-site/lib/dynamicExperience/tenant';
import { resolveShortcodeEnvironment } from './resolveEnvironment';
import { isServerRenderEnabled, serverRenderedAttr } from './serverRender';
import type { DynamicExperienceShortcodeEnrichment } from './types';

/** Placements allowed when `placement` is required on the shortcode (non-empty). */
const KNOWN_SLOT_PLACEMENTS: readonly Exclude<SlotPlacementId, ''>[] = [
  'aboveTheFold',
  'belowTheFold',
  'sidebar',
  'bottomOfPage',
  'el',
  'dashboard',
  'personalizedQdf',
  'inContent',
];

function parseRequiredPlacement(raw?: string): SlotPlacementId | null {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return null;
  return KNOWN_SLOT_PLACEMENTS.includes(trimmed as Exclude<SlotPlacementId, ''>)
    ? (trimmed as SlotPlacementId)
    : null;
}

function parseBool(raw?: string, defaultValue = false): boolean {
  if (raw === undefined || raw === '') return defaultValue;
  return ['true', '1', 'yes'].includes(String(raw).toLowerCase().trim());
}

/** Set only when `from-step-index` is present and valid; otherwise undefined (library default). */
function parseOptionalPositiveInt(raw?: string): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const trimmed = String(raw).trim();
  if (trimmed === '') return undefined;
  const n = Number.parseInt(trimmed, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function resolveTenantFromAttrs(kebab?: string, camel?: string): string {
  const fromKebab = kebab?.trim() ?? '';
  const fromCamel = camel?.trim() ?? '';
  return fromKebab || fromCamel;
}

/** Flow id of the parameterized rankings DSL (successor to `[hen-ui-rankings]`). */
const RANKINGS_DSL_FLOW_ID = 'rankings-dsl';

/**
 * Legacy sponsored parity for the rankings DSL.
 *
 * The legacy `[hen-ui-rankings]` adapter reserved sponsored slots at positions
 * `[3, 9]` whenever the shortcode omitted `sponsoredpositions` (EDUN-13215), so
 * sponsored cards surfaced on every rankings page without a per-page attribute.
 * The `rankings-dsl` data source binds `sponsoredPositions` / `includeSponsored`
 * purely to `context.params.*` with no config-level fallback, so an
 * un-parameterized page would send neither and render organic-only — a
 * regression from legacy.
 *
 * Mirror the legacy default here: when the flow is `rankings-dsl` and the author
 * did not supply the params, enable sponsored at `[3, 9]`. Explicit
 * `param-sponsored-positions` / `param-include-sponsored` always win, so a page
 * can still override positions or opt out via `param-include-sponsored="false"`.
 *
 * Interim bridge until DSL configs support declarative param defaults; once that
 * lands the default moves into the config and this special-case is removed.
 */
const DEFAULT_SPONSORED_POSITIONS: readonly number[] = [3, 9];

function applyRankingsSponsoredDefaults(
  params: FlowContextParams | undefined,
  forcedFlowId: string | undefined,
): FlowContextParams | undefined {
  if (forcedFlowId !== RANKINGS_DSL_FLOW_ID) return params;

  const withDefaults: FlowContextParams = params ? { ...params } : {};

  const hasPositions =
    Array.isArray(withDefaults.sponsoredPositions) &&
    withDefaults.sponsoredPositions.length > 0;
  if (!hasPositions) {
    withDefaults.sponsoredPositions = [...DEFAULT_SPONSORED_POSITIONS];
  }

  // Author-set `false` (opt-out) is respected; only an absent flag defaults on.
  if (typeof withDefaults.includeSponsored !== 'boolean') {
    withDefaults.includeSponsored = true;
  }

  return withDefaults;
}

/** `[tenant]` segment from the App Router (e.g. `psychology.org`). */
function useRouteTenantSlug(): string {
  const params = useParams<{ tenant?: string | string[] }>();
  const raw = params?.tenant;
  const segment = Array.isArray(raw) ? raw[0] : raw;
  return typeof segment === 'string' && segment.trim() ? segment.trim() : '';
}

export interface DynamicExperienceShortcodeAdapterProps {
  placement?: string;
  'tenant-id'?: string;
  tenantId?: string;
  'forced-flow-id'?: string;
  'jump-to-step-id'?: string;
  'entry-step-id'?: string;
  'debug-result'?: string;
  /** When true, same as discover full-page layout (`isFullPageExp`). */
  'full-page'?: string;
  'from-step-index'?: string;
  environment?: string;
  /** When false, skip loading placeholder (matches discover). Default true for in-content embeds. */
  'needs-placeholder'?: string;
  /**
   * Opt this instance into the server flow prefetch. Default false, so the flow
   * loads in the browser unless a page asks for the server path. Requires
   * `forced-flow-id`; the server cannot prefetch a Monarch-selected flow.
   */
  'server-render'?: string;
  dcs?: DCSContext;
  enrichments?: {
    data?: DynamicExperienceShortcodeEnrichment | null;
    error?: Error | null;
  };
  [key: string]: unknown;
}

export function DynamicExperienceShortcodeAdapter({
  placement: placementRaw,
  'tenant-id': tenantIdKebab,
  tenantId: tenantIdCamel,
  'forced-flow-id': forcedFlowId,
  'jump-to-step-id': jumpToStepId,
  'entry-step-id': entryStepId,
  'debug-result': debugResultRaw,
  'full-page': fullPageRaw,
  'from-step-index': fromStepIndexRaw,
  environment: environmentRaw,
  'needs-placeholder': needsPlaceholderRaw,
  'server-render': serverRenderRaw,
  dcs,
  enrichments,
  ...rest
}: DynamicExperienceShortcodeAdapterProps) {
  const routeTenant = useRouteTenantSlug();
  const { siteConfig, monarchExperience } = useGlobalProps();
  const { snapshot } = useRequestContextBridge();
  const placement = parseRequiredPlacement(placementRaw);
  const {
    data: monarchSlot,
    isLoading: isMonarchSlotLoading,
    error: monarchSlotError,
  } = useMonarchSlotPlacement<MonarchSlotFlowData>(placement ?? undefined);

  const fromAttrs = resolveTenantFromAttrs(tenantIdKebab, tenantIdCamel);
  const trimmedTenant = fromAttrs || routeTenant;

  const explicitForcedFlowId = useMemo(
    () => forcedFlowId?.trim() || undefined,
    [forcedFlowId],
  );

  // The flow id that actually reaches `<DynamicExperience>` — the explicit CMS
  // `forced-flow-id` when present, otherwise the Monarch-slot-assigned id. The
  // rankings sponsored defaults gate on THIS (not `explicitForcedFlowId`) so a
  // placement Monarch resolves to `rankings-dsl` without a CMS attribute still
  // gets the legacy `[3,9]` defaults. `resolveEffectiveForcedFlowId` is a pure
  // function, so it's safe to compute here (before the params memo) from the
  // already-resolved Monarch slot.
  const effectiveForcedFlowId = resolveEffectiveForcedFlowId(
    explicitForcedFlowId,
    monarchSlot,
    isMonarchSlotLoading,
  );

  // `rest` captures pass-through attrs (e.g. `param-*`); the collector reads
  // only `param-*` keys and ignores `children`/`enrichments`. Key the memo off
  // the small collected result — never `rest` — so a balanced-tag `children`
  // (React nodes, potentially circular via `_owner`) never reaches
  // `JSON.stringify`.
  const collectedParams = applyRankingsSponsoredDefaults(
    collectContextParams(rest),
    effectiveForcedFlowId,
  );
  const paramsKey = JSON.stringify(collectedParams ?? null);
  const params = useMemo(() => collectedParams, [paramsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const lastReportedMonarchErrorSig = useRef<string | null>(null);

  useEffect(() => {
    if (!monarchSlotError || placement === null) return;

    const sig = `${placement}|${monarchSlotError.message}|${
      monarchSlotError.traceId ?? ''
    }`;
    if (lastReportedMonarchErrorSig.current === sig) return;
    lastReportedMonarchErrorSig.current = sig;

    const message = `Monarch slot resolution failed for placement ${placement}: ${monarchSlotError.message}`;
    newrelic.noticeError?.(new Error(message), {
      placement,
      tenantId: trimmedTenant || '',
      monarchTraceId: monarchSlotError.traceId ?? '',
      monarchTimeout: monarchSlotError.timeout,
    });
    logger.warn(
      { err: monarchSlotError, placement, tenantId: trimmedTenant },
      message,
    );
  }, [monarchSlotError, placement, trimmedTenant]);

  if (placement === null) return null;

  if (!trimmedTenant) return null;

  const environment = resolveShortcodeEnvironment(
    environmentRaw,
    getEnvironment(),
  );
  const debugResult = parseBool(debugResultRaw, false);
  const isFullPageExp = parseBool(fullPageRaw, false);
  const fromStepIndex = parseOptionalPositiveInt(fromStepIndexRaw);
  const needsPlaceholder = parseBool(needsPlaceholderRaw, true);
  const tenantProp = buildDynamicExperienceTenant(siteConfig ?? {});
  // Enforced here as well as in `getEnrichedData` so the opt-in is a real kill
  // switch: an instance that did not ask for the server path never seeds DE,
  // whatever the enrichment pipeline handed down.
  const serverRenderRequested = isServerRenderEnabled(serverRenderRaw);
  const serverData =
    serverRenderRequested &&
    explicitForcedFlowId &&
    enrichments?.data?.flowId === explicitForcedFlowId
      ? enrichments.data
      : undefined;
  const serverRenderSeeded = Boolean(serverData?.initialSteps);

  // An explicit `param-degree/category/subject` triad on the shortcode scopes
  // the placement to the page topic and overrides the page moneyMeta DCS. The
  // override is all-or-nothing (via `toDcsContext`): a partial triad falls back
  // to the moneyMeta-derived `dcs` prop so no partial DCS is ever sent.
  const effectiveDcs = dcsContextFromParams(params) ?? dcs;

  // Without a source key the flow tenant falls back to the route slug
  // (`resolveFlowTenantId`). An explicit `forced-flow-id` names the flow
  // outright, so the slug is a legitimate scope to try and the client fetch can
  // own the outcome — the same contract as the `/[tenant]/de/[flowId]` route,
  // which always carries a fixed flow id and mounts regardless. A
  // Monarch-selected flow gets no such guarantee: a slug Dynamic Flows does not
  // recognize resolves to the shared `default` tenant, which would serve another
  // tenant's experience, so decline to mount.
  if (!tenantProp.tagularSourceKey) {
    const canFallBackToSlug = Boolean(explicitForcedFlowId);
    logger.warn(
      {
        tenantId: trimmedTenant,
        placement,
        forcedFlowId: explicitForcedFlowId,
      },
      'DynamicExperience shortcode: missing siteConfig.tagularSourceKey; ' +
        (canFallBackToSlug
          ? 'resolving the flow tenant from the route slug.'
          : 'skipping mount to avoid mis-routing a Monarch-selected flow to ' +
            'the default tenant.'),
    );
    if (!canFallBackToSlug) return null;
  }

  // Mount DE immediately and let it own the placeholder. While Monarch is still
  // resolving the flow id (and the CMS didn't force one), `awaitingFlowId` holds
  // DE in its placement-sized placeholder without firing a fetch — so it can't
  // race a stale `default-${placement}` flow, and the reserved space is present
  // from first render. That avoids the layout shift the old deferred-mount
  // approach caused (zero-height wrapper until Monarch resolved).
  const awaitingFlowId = !explicitForcedFlowId && isMonarchSlotLoading;

  return (
    <div
      data-testid='hen-ui-dynamic-experience-root'
      data-server-rendered={serverRenderedAttr(serverRenderSeeded)}
      className='w-full'
    >
      <DynamicExperienceErrorBoundary>
        <DynamicExperience
          experienceType='shortcode'
          tenantId={trimmedTenant}
          tenant={tenantProp}
          siteConfigs={siteConfig?.configs}
          placement={placement}
          forcedFlowId={effectiveForcedFlowId}
          awaitingFlowId={awaitingFlowId}
          jumpToStepId={jumpToStepId?.trim() || undefined}
          entryStepId={entryStepId?.trim() || undefined}
          debugResult={debugResult}
          isFullPageExp={isFullPageExp}
          fromStepIndex={fromStepIndex}
          environment={environment}
          needsPlaceholder={needsPlaceholder}
          dcs={effectiveDcs}
          params={params}
          initialSteps={serverData?.initialSteps}
          taxonomySeed={serverData?.taxonomySeed}
          // Monarch identity: `slotId` is the `placement` above;
          // `experienceVariant` is derived in-package.
          slotName={monarchSlot?.slotName}
          slotVariant={monarchSlot?.value?.PersonalizedAssetType ?? 'default'}
          experienceName={monarchExperience?.experienceName}
          requestContext={snapshot}
        />
      </DynamicExperienceErrorBoundary>
    </div>
  );
}
